import { useCallback, useEffect, useState } from 'react';
import { useProvince } from '../contexts/ProvinceContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { _resetDragonStatusCache } from '../hooks/useDragonStatus';
import {
  getDragonStatus,
  startDragonProject,
  fundDragon,
  sendDragon,
  setDragonSendMode,
  slayDragon,
  cancelDragon,
} from '../api/dragon';
import type {
  DragonStatus,
  DragonType,
  DragonSendMode,
} from '../types/dragon';
import { Spinner } from '../components/Spinner';
import { MobileDragonsShell } from '../components/dragons/MobileDragonsShell';
import { DesktopDragonsShell } from '../components/dragons/DesktopDragonsShell';
import { formatNumber } from '../utils/format';
import { formatDuration } from '../utils/formatDuration';
import { deriveReturnTimeFraction } from '../utils/tickCountdown';
import { DRAGON_MAX_CONTRIBUTION_PCT, DRAGON_SOLDIER_HP_DAMAGE, DRAGON_SPECIALIST_HP_DAMAGE } from '../constants/dragon';

/** SF-153: replaces the single `busy: boolean` — a shared boolean can't tell a screen reader (or
 *  a test) which of up to three stacked buttons in a card is in flight. */
export type DragonActionKey = 'start' | 'contribute' | 'send' | 'toggleMode' | 'cancel' | 'slay';

/**
 * SF-153: named, fully-typed props shared by MobileDragonsShell/DesktopDragonsShell — the
 * ExploreShellProps/SciencesShellProps/ThieveryShellProps precedent. State stays hoisted here
 * (not per-shell) so a breakpoint flip mid-form-fill doesn't lose in-progress input.
 */
export interface DragonsShellProps {
  status: DragonStatus | null;
  isLoading: boolean;
  error: string | null;
  busyAction: DragonActionKey | null;
  isMonarch: boolean;
  hasActive: boolean;
  callerIsAttacker: boolean;
  callerIsTarget: boolean;
  fundingProgress: number;
  availableGold: number;
  atHomeSoldiers: number;
  atHomeSpec: number;
  ticksRemainingText: string | null;
  myProvinceId: string;
  targetKingdomNumber: string;
  setTargetKingdomNumber: (value: string) => void;
  targetIslandNumber: string;
  setTargetIslandNumber: (value: string) => void;
  dragonType: DragonType;
  setDragonType: (value: DragonType) => void;
  sendMode: DragonSendMode;
  setSendMode: (value: DragonSendMode) => void;
  fundAmount: string;
  setFundAmount: (value: string) => void;
  slaySoldiers: string;
  setSlaySoldiers: (value: string) => void;
  slaySpecs: string;
  setSlaySpecs: (value: string) => void;
  contributionCapRemaining: number;
  maxContributionAmount: number;
  maxUsefulSoldiers: number;
  maxUsefulSpecialists: number;
  mutationVersion: number;
  showCancelConfirm: boolean;
  setShowCancelConfirm: (value: boolean) => void;
  showSlayConfirm: boolean;
  setShowSlayConfirm: (value: boolean) => void;
  slayRequiresTypedConfirm: boolean;
  handleStart: () => void;
  handleContribute: () => void;
  handleMaxContribute: () => void;
  handleSend: () => void;
  handleToggleMode: () => void;
  handleCancelRequest: () => void;
  handleCancelConfirm: () => void;
  handleSlayRequest: () => void;
  handleSlayConfirm: () => void;
  handleMaxSoldiers: () => void;
  handleMaxSpecs: () => void;
}

export function Dragons() {
  const { province } = useProvince();
  const { worldId } = useAuth();
  const { addToast } = useToast();
  const platform = useAppShellPlatform();

  const kingdomId = province?.metadata?.location
    ? `${province.metadata.location.kingdom}:${province.metadata.location.island}`
    : null;
  const myProvinceId = province?.metadata?.id ?? null;
  const isMonarch = province?.metadata?.isMonarch ?? false;
  const availableGold = Math.floor(province?.resources?.gold ?? 0);
  const atHomeSoldiers = Math.floor(province?.population?.soldiers ?? 0);
  const atHomeDef = Math.floor(province?.population?.defenseSpecialists ?? 0);
  const atHomeOff = Math.floor(province?.population?.offenseSpecialists ?? 0);
  const atHomeSpec = atHomeDef + atHomeOff;

  const [status, setStatus] = useState<DragonStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<DragonActionKey | null>(null);
  const [mutationVersion, setMutationVersion] = useState(0);

  // Form state
  const [targetKingdomNumber, setTargetKingdomNumber] = useState('');
  const [targetIslandNumber, setTargetIslandNumber] = useState('');
  const [dragonType, setDragonType] = useState<DragonType>('EMERALD');
  const [sendMode, setSendMode] = useState<DragonSendMode>('AUTO_ON_FUNDED');
  const [fundAmount, setFundAmount] = useState('');
  const [slaySoldiers, setSlaySoldiers] = useState('');
  const [slaySpecs, setSlaySpecs] = useState('');

  // Confirm-modal state (Open Question 1 — replaces window.confirm())
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showSlayConfirm, setShowSlayConfirm] = useState(false);
  const [slayRequiresTypedConfirm, setSlayRequiresTypedConfirm] = useState(false);

  const refresh = useCallback(async (): Promise<boolean> => {
    if (!worldId || !kingdomId) return false;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getDragonStatus(worldId, kingdomId);
      setStatus(data);
      return true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load dragon status');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [worldId, kingdomId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!worldId || !kingdomId || !myProvinceId || !province) {
    return <div style={{ padding: 16 }}><Spinner /></div>;
  }

  // Handlers/computed values declared here, as plain consts (NOT useCallback) — the guard above
  // has already narrowed worldId/myProvinceId to non-null (Critical Issue 12 note: declaring
  // these as useCallback above the guard would lose that narrowing and force `!` assertions in
  // every handler body).

  const hasActive: boolean = Boolean(status?.hasProject && status.state && !['SLAIN', 'EXPIRED', 'CANCELLED'].includes(status.state));
  const callerIsAttacker = status?.role === 'ATTACKER';
  const callerIsTarget = status?.role === 'TARGET';
  const fundingProgress = status?.totalCost && status?.amountFunded
    ? Math.min(100, (status.amountFunded / status.totalCost) * 100)
    : 0;

  const existingContribution = status?.contributions?.find(c => c.provinceId === myProvinceId)?.amount ?? 0;
  const contributionCapRemaining = Math.max(0, Math.floor((status?.totalCost ?? 0) * DRAGON_MAX_CONTRIBUTION_PCT) - existingContribution);
  const projectRemaining = Math.max(0, (status?.totalCost ?? 0) - (status?.amountFunded ?? 0));
  const maxContributionAmount = Math.max(0, Math.min(contributionCapRemaining, projectRemaining, availableGold));

  const maxUsefulSoldiers = Math.min(atHomeSoldiers, Math.ceil((status?.hp ?? 0) / DRAGON_SOLDIER_HP_DAMAGE));
  const maxUsefulSpecialists = Math.min(atHomeSpec, Math.ceil((status?.hp ?? 0) / DRAGON_SPECIALIST_HP_DAMAGE));

  const ticksRemainingText = status?.expiresAtTick != null
    ? formatDuration(Math.max(0, status.expiresAtTick - province.sync.currentTick), {
        returnTimeFraction: deriveReturnTimeFraction(province.sync),
        tickIntervalMs: province.sync.tickIntervalMs,
      })
    : null;

  const wrap = async (key: DragonActionKey, fn: () => Promise<void>) => {
    if (busyAction !== null) return;
    setBusyAction(key);
    try {
      await fn();
      _resetDragonStatusCache();
      setMutationVersion(v => v + 1);
      const refreshed = await refresh();
      if (!refreshed) {
        addToast('Action completed, but the latest status could not be loaded — try refreshing.', 'failure');
      }
    } catch (err: unknown) {
      addToast(err instanceof Error ? err.message : 'Action failed', 'failure');
    } finally {
      setBusyAction(null);
    }
  };

  const handleStart = () => {
    if (!targetKingdomNumber || !targetIslandNumber) return;
    wrap('start', async () => {
      const targetKingdomId = `${targetKingdomNumber}:${targetIslandNumber}`;
      await startDragonProject(worldId, myProvinceId, targetKingdomId, dragonType, sendMode);
      addToast('Dragon project started', 'success');
    });
  };

  const handleContribute = () => {
    const requested = Number(fundAmount || 0);
    if (requested <= 0) return;
    wrap('contribute', async () => {
      const result = await fundDragon(worldId, myProvinceId, requested);
      setFundAmount('');
      const suffix = result.charged < requested ? ' — you hit your per-province cap' : '';
      addToast(`Contributed ${formatNumber(result.charged)} gc${suffix}`, 'success');
    });
  };

  const handleMaxContribute = () => {
    if (contributionCapRemaining <= 0) return;
    setFundAmount(String(maxContributionAmount));
  };

  const handleSend = () => {
    wrap('send', async () => {
      await sendDragon(worldId, myProvinceId);
      addToast('Dragon sent!', 'success');
    });
  };

  const handleToggleMode = () => {
    wrap('toggleMode', async () => {
      const next = status?.sendMode === 'AUTO_ON_FUNDED' ? 'MANUAL' : 'AUTO_ON_FUNDED';
      await setDragonSendMode(worldId, myProvinceId, next);
      addToast('Send mode updated', 'success');
    });
  };

  const handleCancelRequest = () => {
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = () => {
    setShowCancelConfirm(false);
    wrap('cancel', async () => {
      await cancelDragon(worldId, myProvinceId);
      addToast('Project cancelled', 'success');
    });
  };

  const handleSlayRequest = () => {
    const s = Number(slaySoldiers || 0);
    const sp = Number(slaySpecs || 0);
    if (s + sp <= 0) return;
    const totalAtHome = atHomeSoldiers + atHomeSpec;
    // totalAtHome === 0 has no meaningful "50% of at-home units" to compare against — treat any
    // positive request as below the threshold rather than dividing by zero into a true comparison.
    setSlayRequiresTypedConfirm(totalAtHome > 0 && s + sp >= 0.5 * totalAtHome);
    setShowSlayConfirm(true);
  };

  const handleSlayConfirm = () => {
    setShowSlayConfirm(false);
    const s = Number(slaySoldiers || 0);
    const sp = Number(slaySpecs || 0);
    wrap('slay', async () => {
      const result = await slayDragon(worldId, myProvinceId, s, sp);
      setSlaySoldiers('');
      setSlaySpecs('');
      if (result.dragonSlain) {
        addToast('Dragon slain!', 'success');
      } else {
        const damage = result.soldiersLost * DRAGON_SOLDIER_HP_DAMAGE + result.specialistsLost * DRAGON_SPECIALIST_HP_DAMAGE;
        addToast(`Dealt ${formatNumber(damage)} damage — ${formatNumber(result.hpRemaining)} HP remaining`, 'success');
      }
    });
  };

  const handleMaxSoldiers = () => {
    if (maxUsefulSoldiers <= 0) return;
    setSlaySoldiers(String(maxUsefulSoldiers));
  };

  const handleMaxSpecs = () => {
    if (maxUsefulSpecialists <= 0) return;
    setSlaySpecs(String(maxUsefulSpecialists));
  };

  const shellProps: DragonsShellProps = {
    status,
    isLoading,
    error,
    busyAction,
    isMonarch,
    hasActive,
    callerIsAttacker,
    callerIsTarget,
    fundingProgress,
    availableGold,
    atHomeSoldiers,
    atHomeSpec,
    ticksRemainingText,
    myProvinceId,
    targetKingdomNumber,
    setTargetKingdomNumber,
    targetIslandNumber,
    setTargetIslandNumber,
    dragonType,
    setDragonType,
    sendMode,
    setSendMode,
    fundAmount,
    setFundAmount,
    slaySoldiers,
    setSlaySoldiers,
    slaySpecs,
    setSlaySpecs,
    contributionCapRemaining,
    maxContributionAmount,
    maxUsefulSoldiers,
    maxUsefulSpecialists,
    mutationVersion,
    showCancelConfirm,
    setShowCancelConfirm,
    showSlayConfirm,
    setShowSlayConfirm,
    slayRequiresTypedConfirm,
    handleStart,
    handleContribute,
    handleMaxContribute,
    handleSend,
    handleToggleMode,
    handleCancelRequest,
    handleCancelConfirm,
    handleSlayRequest,
    handleSlayConfirm,
    handleMaxSoldiers,
    handleMaxSpecs,
  };

  return platform === 'mobile'
    ? <MobileDragonsShell {...shellProps} />
    : <DesktopDragonsShell {...shellProps} />;
}
